import { useState, useEffect, useRef } from 'react';
import { events } from 'aws-amplify/data';
import { useOktaAuth } from "@okta/okta-react";
import { usePartnerManagerAPI } from '../components/as2-onboarding-component/hooks/useClient';
import { DynamoDBItemMinimal } from '../components/partner-manager/types';
/**
 * Transforms backend task event to frontend task format
 * FGIV1AI - Works for both http snapshot and appsync events
 */
const transformTask = (eventData: any): DynamoDBItemMinimal | null => {
  try {
    // Only parse if it's an AppSync event with 'event' field
    if (eventData.event) {
      return typeof eventData.event === 'string' 
        ? JSON.parse(eventData.event) 
        : eventData.event;
    }
    
    // Return as-is (already a task object)
    return eventData;
  } catch (error) {
    console.error('Error parsing task event:', error, eventData);
    return null;
  }
};

interface UseTaskQueueEventsReturn {
  tasks: DynamoDBItemMinimal[];
  loading: boolean;
  error: any;
  isConnected: boolean;
}

const useTaskQueueEvents = (): UseTaskQueueEventsReturn => {
  const [tasks, setTasks] = useState<DynamoDBItemMinimal[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  
  const channelRef = useRef<any>(null);
  const subscriptionRef = useRef<any>(null);

  const { oktaAuth } = useOktaAuth();
  const partnerManagerClient = usePartnerManagerAPI(oktaAuth.getAccessToken());

  useEffect(() => {
    let mounted = true;

    const initializeTaskQueue = async (): Promise<void> => {
      try {
        // Step 1: Fetch snapshot from HTTP endpoint (CRITICAL)
        console.log('Fetching task snapshot...');
        const snapshotResponse = await partnerManagerClient.get('/tasks/snapshot');
        
        if (!mounted) return;

        // Transform and set tasks from snapshot
        const snapshotTasks = snapshotResponse.data.tasks || [];
        const transformedSnapshot = snapshotTasks
          .map(transformTask)
          .filter(Boolean) as DynamoDBItemMinimal[];
        
        setTasks(transformedSnapshot);
        setLoading(false);
        console.log(`Loaded ${transformedSnapshot.length} tasks from snapshot`);

        // Step 2: Connect to AppSync Events for real-time updates (OPTIONAL)
        try {
          const channelPath = process.env.REACT_APP_TASK_QUEUE_CHANNEL || '/events/task-queue';
          
          if (!process.env.REACT_APP_TASK_QUEUE_CHANNEL) {
            console.warn(
              `REACT_APP_TASK_QUEUE_CHANNEL not set. Using default channel: ${channelPath}`
            );
          }

          console.log(`Connecting to task queue channel: ${channelPath}`);      
          const channel = await events.connect(channelPath);
          
          if (!mounted) return;

          channelRef.current = channel;
          setIsConnected(true);

          subscriptionRef.current = channel.subscribe({
            next: (data: any) => {
              if (!mounted) return;

              console.log('Received task queue event:', data);
              
              // Transform the task data
              const transformedTask = transformTask(data);
              
              if (!transformedTask) {
                console.warn('Failed to transform task, skipping:', data);
                return;
              }

              setTasks((prevTasks) => {
                    const existingIndex = prevTasks.findIndex(t => t.id === transformedTask.id);
                    if (existingIndex !== -1) {
                      // Update existing task
                      const updated = [...prevTasks];
                      updated[existingIndex] = transformedTask;
                      return updated;
                    }
                    
                    // Add new task
                    return [...prevTasks, transformedTask];
                  });
                  
                  },
            error: (err: any) => {
              if (!mounted) return;
              console.warn('AppSync subscription error (non-critical):', err);
              setIsConnected(false);
              // DON'T set error state - data already loaded from snapshot
            },
          });
        } catch (eventsError) {
          // AppSync connection failed, but snapshot data is already loaded
          console.warn('Failed to connect to AppSync Events (non-critical):', eventsError);
          console.log('Continuing with snapshot data only');
          // DON'T set error state
          // DON'T throw
        }

      } catch (err) {
        // HTTP snapshot failed - this IS an error
        if (!mounted) return;
        console.error('Failed to load snapshot:', err);
        setError(err);
        setLoading(false);
      }
    };

    initializeTaskQueue();

    return () => {
      mounted = false;
      if (subscriptionRef.current) {
        try {
          subscriptionRef.current.unsubscribe();
          console.log('Unsubscribed from task queue events');
        } catch (err) {
          console.error('Error unsubscribing:', err);
        }
      }
      if (channelRef.current && typeof channelRef.current.close === 'function') {
        try {
          channelRef.current.close();
          console.log('Closed task queue channel');
        } catch (err) {
          console.error('Error closing channel:', err);
        }
      }
    };
  }, []);

  return { tasks, loading, error, isConnected };
};

export default useTaskQueueEvents;